'use client';

import useSWR from 'swr';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';

import { paths } from 'src/routes/paths';
import { NyttFärgTema } from 'src/theme/NyttFärgtema';
import axiosInstance, { endpoints } from 'src/lib/axios';
import type { IAvdelning } from 'src/types/nya/förskola/FörskolaTyper';

import type { KortProps } from '../kort-katalog';
import DomainCard from '../components/domain-card';
import { MiniList, MiniRow, swrOptions } from './kort-primitiver';

// ----------------------------------------------------------------------

export default function BarnKort({ orgId }: KortProps) {
    const { data: avdelningar, isLoading } = useSWR<IAvdelning[]>(
        orgId ? ['förskola-avdelningar', orgId] : null,
        () => axiosInstance.get(endpoints.förskola.avdelningar(orgId)).then((r) => r.data),
        swrOptions
    );

    const aktiva = (avdelningar ?? []).filter((a) => a.aktiv !== false);
    const totaltBarn = aktiva.reduce((sum, a) => sum + (a.antalBarn ?? 0), 0);

    if (isLoading && !avdelningar) {
        return (
            <DomainCard
                title="Barn & avdelningar"
                icon="solar:users-group-rounded-bold"
                theme={NyttFärgTema.Kombinationer[3]}
                href={paths.dashboard.förskola.avdelningar}
                primaryMetric={{ value: '—', label: 'barn' }}
            >
                <Stack spacing={0.75}>
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                </Stack>
            </DomainCard>
        );
    }

    if (aktiva.length === 0) {
        return (
            <DomainCard
                title="Barn & avdelningar"
                icon="solar:users-group-rounded-bold"
                theme={NyttFärgTema.Kombinationer[3]}
                href={paths.dashboard.förskola.avdelningar}
                emptyMessage="Lägg till avdelningar och registrera barn för att se en översikt här."
            />
        );
    }

    return (
        <DomainCard
            title="Barn & avdelningar"
            icon="solar:users-group-rounded-bold"
            theme={NyttFärgTema.Kombinationer[3]}
            href={paths.dashboard.förskola.avdelningar}
            primaryMetric={{ value: totaltBarn, label: totaltBarn === 1 ? 'barn' : 'barn' }}
            secondaryMetric={{ value: `${aktiva.length}`, label: aktiva.length === 1 ? 'avdelning' : 'avdelningar' }}
        >
            <MiniList>
                {aktiva.slice(0, 4).map((a) => (
                    <MiniRow
                        key={a.id}
                        primary={a.namn}
                        secondary={a.åldersgrupp === 'SMÅBARN' ? 'Småbarn' : a.åldersgrupp === 'STORBARN' ? 'Storbarn' : 'Blandad'}
                        trailing={`${a.antalBarn ?? 0} barn`}
                    />
                ))}
            </MiniList>
        </DomainCard>
    );
}
